/**
 * ISSUE-1299. An admin renames a custom game from the Game tab, after v1's
 * EditableTextRow on the strip Game tab. League games keep the feed's
 * matchup and never show this.
 */
import { Button, confirmDestructive, Dialog, Input } from "@sportsmet/ui";
import { type ReactNode, useEffect, useState } from "react";

import {
  STRIP_CANCEL_LABEL,
  STRIP_CUSTOM_GAME_NAME_LABEL,
  STRIP_CUSTOM_GAME_NAME_MAX_LENGTH,
  STRIP_DISCARD_GAME_NAME_BODY,
  STRIP_DISCARD_LABEL,
  STRIP_DISCARD_TITLE,
  STRIP_RENAME_GAME_EMPTY_ERROR,
  STRIP_RENAME_GAME_SAVE_LABEL,
  STRIP_RENAME_GAME_TITLE,
} from "./stripCardCopy";

export type typeStripRenameGameSheetProps = {
  open: boolean;
  currentTitle: string;
  isBusy: boolean;
  onClose: () => void;
  onSave: (title: string) => Promise<boolean>;
};

export const StripRenameGameSheet = (
  props: typeStripRenameGameSheetProps,
): ReactNode => {
  const [title, setTitle] = useState(props.currentTitle);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    if (props.open) {
      setTitle(props.currentTitle);
      setIsSubmitted(false);
    }
  }, [props.open, props.currentTitle]);

  const isBlank = title.trim() === "";
  const isDirty = title.trim() !== props.currentTitle;

  async function requestClose(): Promise<void> {
    if (!isDirty) {
      props.onClose();
      return;
    }
    const isConfirmed = await confirmDestructive({
      title: STRIP_DISCARD_TITLE,
      body: STRIP_DISCARD_GAME_NAME_BODY,
      confirmLabel: STRIP_DISCARD_LABEL,
    });
    if (isConfirmed) props.onClose();
  }

  function save(): void {
    setIsSubmitted(true);
    if (isBlank) return;
    if (!isDirty) {
      props.onClose();
      return;
    }
    void props.onSave(title).then((isDone) => {
      if (isDone) props.onClose();
    });
  }

  return (
    <Dialog
      open={props.open}
      onOpenChange={(open) => {
        if (!open) props.onClose();
      }}
      title={STRIP_RENAME_GAME_TITLE}
      isDirty={isDirty}
      onRequestClose={() => {
        void requestClose();
      }}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => {
              void requestClose();
            }}
          >
            {STRIP_CANCEL_LABEL}
          </Button>
          <Button
            variant="primary"
            data-testid="strip-rename-game-save"
            loading={props.isBusy}
            onClick={save}
          >
            {STRIP_RENAME_GAME_SAVE_LABEL}
          </Button>
        </>
      }
    >
      <div data-testid="strip-rename-game-sheet">
        <Input
          label={STRIP_CUSTOM_GAME_NAME_LABEL}
          maxLength={STRIP_CUSTOM_GAME_NAME_MAX_LENGTH}
          value={title}
          error={
            isSubmitted && isBlank ? STRIP_RENAME_GAME_EMPTY_ERROR : undefined
          }
          data-testid="strip-rename-game-name"
          onChange={(event) => {
            setTitle(event.target.value);
          }}
        />
      </div>
    </Dialog>
  );
};
